import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@common/services/AuthService/AuthService';
import { ModalService } from '@common/services/ModalService/ModalService';
import { TaskService } from '@common/services/TaskService/TaskService';
import { EmployeeShort } from '@common/models/employeeShort.model';
import { AddTaskData } from '@common/models/addTaskData.model';
import { TaskAddInfo } from '@common/models/taskAddInfo.model';
import { TaskDetails } from '@common/models/taskDetails.model';
import { BreadcrumbLevel } from '@common/models/breadcrumbLevel.model';

@Component({
  templateUrl: './addEditTaskView.component.html',
  styleUrls: ['./addEditTaskView.component.scss'],
})
export class AddEditTaskViewComponent implements OnInit {
  attemptedToSubmit = false;
  submitted = false;
  public clientKey: string;
  public areaKey: string;
  public taskKey: string;
  public taskDetails: TaskDetails;
  public mode: string;
  public breadcrumbLevels: BreadcrumbLevel[] = [];
  public employees: EmployeeShort[] = [];
  addEditTaskForm: FormGroup;
  minDate = new Date(new Date().setHours(0, 0, 0, 0));
  previousRepeatPeriod = 'single';

  constructor(
    public authService: AuthService,
    public router: Router,
    private route: ActivatedRoute,
    public modalService: ModalService,
    public taskService: TaskService,
  ) {}

  static convertDateToLocalNoonISOString(date: Date): string {
    date.setHours(12, 0, 0);
    return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString();
  }

  ngOnInit() {
    this.addEditTaskForm = new FormGroup({
      taskName: new FormControl('', Validators.required),
      employees: new FormControl([]),
      repeatPeriod: new FormControl({
        period: 'single',
        daysOfWeek: {
          daily: false,
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
        },
        dayOfMonth: null,
      }),
      date: new FormControl('', Validators.required),
      dateRange: new FormControl('', Validators.required),
    });

    this.addEditTaskForm.get('repeatPeriod').valueChanges.subscribe((value) => {
      const periodValue = this.addEditTaskForm.get('repeatPeriod').value.controls.period.value;

      if (periodValue !== this.previousRepeatPeriod) {
        this.previousRepeatPeriod = periodValue;
        this.addEditTaskForm.get('repeatPeriod').markAsUntouched();
        this.addEditTaskForm.get('date').markAsUntouched();
        this.addEditTaskForm.get('dateRange').markAsUntouched();

        this.updateDateValidators();
      }
    });

    this.route.params.subscribe((params) => {
      this.clientKey = params['clientKey'];
      this.areaKey = params['areaKey'];
      this.taskKey = params['taskKey'];
      this.mode = this.taskKey ? 'edit' : 'add';

      this.getInitialData();

      if (this.mode === 'edit') {
        this.getTaskData();
      }

      this.updateDateValidators();
    });
  }

  getInitialData() {
    this.taskService.getForAddingTask(this.areaKey)
      .subscribe(
        (data: TaskAddInfo) => {

          if (this.mode === 'add') {
            const breadcrumbLevels = data.breadcrumbLevels[0];

            this.breadcrumbLevels = [
              { clientKey: breadcrumbLevels.clientKey, clientName: breadcrumbLevels.clientName },
              { areaKey: breadcrumbLevels.areaKey, areaName: breadcrumbLevels.areaName },
            ];
          }

          this.employees = data.employees;
        },
        error => this.authService.handleRequestError(error, this.getInitialData.bind(this)),
      );
  }

  getTaskData() {
    this.taskService.getTaskDetails(this.taskKey)
      .subscribe(
        (data: TaskDetails) => {
          this.taskDetails = data;

          this.breadcrumbLevels = [
            { clientKey: data.clientKey, clientName: data.clientName },
            { areaKey: data.areaKey, areaName: data.areaName },
            { taskKey: data.taskKey, taskName: data.taskName },
          ];

          const daily = data.repeat === 'daily';
          const period = daily ? 'weekly' : data.repeat;

          this.addEditTaskForm.patchValue({
            taskName: data.taskName,
            employees: data.employees,
          });

          if (data.repeat) {
            this.addEditTaskForm.patchValue({
              repeatPeriod: {
                period,
                daysOfWeek: {
                  daily,
                  sunday: daily ? true : data.sunday,
                  monday: daily ? true : data.monday,
                  tuesday: daily ? true : data.tuesday,
                  wednesday: daily ? true : data.wednesday,
                  thursday: daily ? true : data.thursday,
                  friday: daily ? true : data.friday,
                  saturday: daily ? true : data.saturday,
                },
                dayOfMonth: data.day,
              },
            });
          }

          if (data.startDate && data.endDate) {
            this.addEditTaskForm.patchValue({
              dateRange: [new Date(data.startDate), new Date(data.endDate)],
            });
          } else if (data.date) {
            this.addEditTaskForm.patchValue({
              date: new Date(data.date),
            });
          }
        });
  }

  updateDateValidators() {
    const periodValue = this.addEditTaskForm.get('repeatPeriod').value.period ?
      this.addEditTaskForm.get('repeatPeriod').value.period :
      this.addEditTaskForm.get('repeatPeriod').value.controls.period.value;

    if (periodValue === 'single') {
      this.addEditTaskForm.get('dateRange').clearValidators();
      this.addEditTaskForm.get('dateRange').updateValueAndValidity();
      this.addEditTaskForm.get('date').setValidators(Validators.required);
      this.addEditTaskForm.get('date').updateValueAndValidity();
    }

    if (periodValue === 'weekly' || periodValue === 'monthly') {
      this.addEditTaskForm.get('date').clearValidators();
      this.addEditTaskForm.get('date').updateValueAndValidity();
      this.addEditTaskForm.get('dateRange').setValidators(Validators.required);
      this.addEditTaskForm.get('dateRange').updateValueAndValidity();
    }
  }

  generateDate(): string {
    const date = this.addEditTaskForm.get('date').value;

    return date ? date.toLocaleDateString() : '';
  }

  generateDateRange(): string {
    let dateRange: string;

    if (this.addEditTaskForm.get('dateRange').value && this.addEditTaskForm.get('dateRange').value.length) {
      const dateFrom = this.addEditTaskForm.get('dateRange').value[0].toLocaleDateString();
      const dateTo = this.addEditTaskForm.get('dateRange').value[1].toLocaleDateString();
      dateRange = `${dateFrom} - ${dateTo}`;
    } else {
      dateRange = '';
    }

    return dateRange;
  }

  onSubmit(): void {
    this.attemptedToSubmit = true;

    for (const control in this.addEditTaskForm.controls) {
      if (this.addEditTaskForm.controls.hasOwnProperty(control)) {
        this.addEditTaskForm.controls[control].markAsTouched();
      }
    }

    if (this.addEditTaskForm.valid && !this.submitted) {
      const formValue = this.addEditTaskForm.value;
      let formValueToSend: AddTaskData = {
        taskName: null,
        employeeKeys: [],
        repeat: null,
        startDate: null,
        endDate: null,
        sunday: null,
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        day: null,
      };

      formValueToSend.taskName = formValue.taskName;
      formValueToSend.employeeKeys = [];

      formValue.employees.forEach(employee => formValueToSend.employeeKeys.push(employee.key));

      formValueToSend.repeat =
        (formValue.repeatPeriod.value.period === 'weekly' && formValue.repeatPeriod.value.daysOfWeek.daily)
          ? 'daily'
          : formValue.repeatPeriod.value.period;

      formValueToSend.startDate =
        (formValue.repeatPeriod.value.period === 'weekly' || formValue.repeatPeriod.value.period === 'monthly')
          ? AddEditTaskViewComponent.convertDateToLocalNoonISOString(formValue.dateRange[0])
          : AddEditTaskViewComponent.convertDateToLocalNoonISOString(formValue.date);

      formValueToSend.endDate =
        (formValue.repeatPeriod.value.period === 'weekly' || formValue.repeatPeriod.value.period === 'monthly')
          ? AddEditTaskViewComponent.convertDateToLocalNoonISOString(formValue.dateRange[1])
          : null;

      if (formValue.repeatPeriod.value.period === 'weekly' && !formValue.repeatPeriod.value.daysOfWeek.daily) {
        formValueToSend = Object.assign(formValueToSend, formValue.repeatPeriod.value.daysOfWeek);
        delete formValueToSend.daily;
      }

      formValueToSend.day =
        (formValue.repeatPeriod.value.period === 'monthly')
          ? formValue.repeatPeriod.value.dayOfMonth
          : null;

      if (this.mode === 'add') {
        this.taskService.addTask(this.areaKey, formValueToSend)
          .subscribe(
            (res) => {
              this.router.navigateByUrl(`/clients/${this.clientKey}/${this.areaKey}`);
              this.modalService.openModal({ type: 'taskAdded' });
            },
            (err) => {
              this.modalService.openModal({ type: 'errorOccurred' });
              this.submitted = false;
            },
          );

        this.submitted = true;
      }

      if (this.mode === 'edit') {
        const proceedTaskEdit = (allTasks: boolean) => {
          this.modalService.closeModal();
          this.taskService.editTask(this.taskKey, formValueToSend, allTasks)
            .subscribe(
              (res) => {
                this.router.navigateByUrl(`/clients/${this.clientKey}/${this.areaKey}/${this.taskKey}`);
                this.modalService.openModal({ type: 'changesSaved' });
              },
              (err) => {
                this.modalService.openModal({ type: 'errorOccurred' });
                this.submitted = false;
              },
            );

          this.submitted = true;
        };

        this.modalService.openModal({
          type: 'editTask',
          data: this.taskDetails.date,
          callback: proceedTaskEdit,
        });
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/clients/views/addEditTaskView/addEditTaskView.component.ts